/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import type { Path } from '@ahm/api-wrappers-core';
import { FloatingLabelInput, FloatingLabelSelect } from '@ahm/react-ui';
import type { ComboboxItem } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  useGetDistricts,
  useGetProvinces,
  useGetWards,
} from '../../api/intercity';

type PickAddressDetailsProps = {
  initAddress?: Path;
  onChangeAddress: (addr: string) => void;
};

const PickAddressDetails = ({ onChangeAddress }: PickAddressDetailsProps) => {
  const tBooking = useTranslations('booking');
  const {
    control,
    reset,
    formState: { errors },
    getValues,
    trigger,
  } = useFormContext();
  const [selectedProvince, setSelectedProvince] = useState<ComboboxItem | null>(
    null
  );
  const [selectedDistrict, setSelectedDistrict] = useState<ComboboxItem | null>(
    null
  );
  const [selectedWard, setSelectedWard] = useState<ComboboxItem | null>(null);

  const { data: provinces } = useGetProvinces();
  const { data: districts } = useGetDistricts(
    {
      provinceId: getValues('nationwide_province_id') ?? '',
    },
    {
      enabled: !!getValues('nationwide_province_id'),
      queryKey: ['districts', getValues('nationwide_province_id')],
    }
  );
  const { data: wards } = useGetWards(
    {
      districtId: getValues('nationwide_district_id') ?? '',
    },
    {
      enabled: !!getValues('nationwide_district_id'),
      queryKey: ['districts', getValues('nationwide_district_id')],
    }
  );

  const provinceOptions = provinces?.map((province) => ({
    value: province._id,
    label: province.name_vn,
  }));

  const districtOptions = districts?.map((district) => ({
    value: district._id,
    label: district.name_vn,
  }));

  const wardOptions = wards?.map((ward) => ({
    value: ward._id,
    label: ward.name_vn,
  }));

  const handleProvinceChange = async (
    _value: string | null,
    option: ComboboxItem | null
  ) => {
    if (option?.value === selectedProvince?.value) {
      // If it's the same, return early without resetting the form fields
      return;
    }

    setSelectedProvince(option);
    setSelectedDistrict(null);
    setSelectedWard(null);

    reset((prev) => ({
      ...prev,
      nationwide_province_id: _value!,
      nationwide_district_id: '',
      nationwide_ward_id: '',
    }));

    await trigger([
      'nationwide_province_id',
      'nationwide_district_id',
      'nationwide_ward_id',
    ]);
  };

  const handleDistrictChange = async (
    _value: string | null,
    option: ComboboxItem | null
  ) => {
    if (option?.value === selectedDistrict?.value) {
      // If it's the same, return early without resetting the form fields
      return;
    }
    setSelectedDistrict(option);
    setSelectedWard(null);

    reset((prev) => ({
      ...prev,
      nationwide_district_id: _value!,
      nationwide_ward_id: '',
    }));

    await trigger(['nationwide_district_id', 'nationwide_ward_id']);
  };

  const handleWardChange = async (
    _value: string | null,
    option: ComboboxItem | null
  ) => {
    if (option?.value === selectedWard?.value) {
      // If it's the same, return early without resetting the form fields
      return;
    }

    setSelectedWard(option);

    reset((prev) => ({
      ...prev,
      nationwide_ward_id: _value!,
    }));

    await trigger('nationwide_ward_id');
  };

  const streetDetails = useWatch({
    name: 'first_label',
    control,
  });

  const [debouncedStreetDetails] = useDebouncedValue(streetDetails, 500);

  const handleAutoFillDetails = () => {
    const provinceId = getValues('nationwide_province_id');
    const districtId = getValues('nationwide_district_id');
    const wardId = getValues('nationwide_ward_id');

    if (!provinceId || !districtId || !wardId) {
      return;
    }

    const province = provinces?.find((p) => p._id === provinceId);
    const district = districts?.find((d) => d._id === districtId);
    const ward = wards?.find((w) => w._id === wardId);

    if (province) {
      setSelectedProvince({
        value: province._id,
        label: province.name_vn,
      });
    }
    if (district) {
      setSelectedDistrict({
        value: district._id,
        label: district.name_vn,
      });
    }
    if (ward) {
      setSelectedWard({
        value: ward._id,
        label: ward.name_vn,
      });
    }
  };

  useEffect(() => {
    let textForGeocoding = '';

    handleAutoFillDetails();

    const addressParts = [
      debouncedStreetDetails,
      selectedWard?.label,
      selectedDistrict?.label,
      selectedProvince?.label,
    ];

    if (!addressParts.some(Boolean)) return;

    textForGeocoding = addressParts.filter(Boolean).join(', ');

    onChangeAddress(textForGeocoding);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWard?.label, debouncedStreetDetails]);

  return (
    <>
      <Controller
        control={control}
        name="nationwide_province_id"
        render={({ field: { onChange, value } }) => (
          <FloatingLabelSelect
            label={tBooking('location.admin.province')}
            data={provinceOptions}
            value={value}
            onChange={(value, option) => {
              onChange(value);

              void handleProvinceChange(value, option);
            }}
            onSearchChange={(value) => {
              if (value === '') {
                onChange(null);
              }
            }}
            searchable
            withAsterisk
            comboboxProps={{
              withinPortal: false,
              position: 'bottom',
              middlewares: { flip: false, shift: false },
            }}
            checkIconPosition="right"
            allowDeselect={false}
            error={errors.nationwide_province_id?.message?.toString()}
          />
        )}
      />

      <Controller
        control={control}
        name="nationwide_district_id"
        render={({ field: { onChange, value } }) => (
          <FloatingLabelSelect
            label={tBooking('location.admin.district')}
            data={districtOptions}
            searchable
            withAsterisk
            comboboxProps={{
              withinPortal: false,
              position: 'bottom',
              middlewares: { flip: false, shift: false },
            }}
            checkIconPosition="right"
            allowDeselect={false}
            maxDropdownHeight={180}
            value={value || null}
            onChange={(value, option) => {
              onChange(value);
              void handleDistrictChange(value, option);
            }}
            onSearchChange={(value) => {
              if (value === '') {
                onChange(null);
              }
            }}
            error={errors.nationwide_district_id?.message?.toString()}
          />
        )}
      />

      <Controller
        control={control}
        name="nationwide_ward_id"
        render={({ field: { onChange, value } }) => (
          <FloatingLabelSelect
            label={tBooking('location.admin.ward')}
            data={wardOptions}
            searchable
            withAsterisk
            comboboxProps={{
              withinPortal: false,
              position: 'bottom',
              middlewares: { flip: false, shift: false },
            }}
            checkIconPosition="right"
            allowDeselect={false}
            maxDropdownHeight={120}
            value={value || null}
            onChange={(value, option) => {
              onChange(value);
              void handleWardChange(value, option);
            }}
            onSearchChange={(value) => {
              if (value === '') {
                onChange(null);
              }
            }}
            error={errors.nationwide_ward_id?.message?.toString()}
          />
        )}
      />

      <Controller
        control={control}
        name="first_label"
        render={({ field: { value, onChange } }) => (
          <FloatingLabelInput
            value={value}
            withAsterisk
            label={tBooking('location.admin.street')}
            onChange={(value) => {
              onChange(value);
            }}
            error={errors.first_label?.message?.toString()}
          />
        )}
      />
    </>
  );
};

export default PickAddressDetails;
